import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import FacturaPreview, { printFactura } from '../components/FacturaPreview';
import { pedidoClienteLabel } from '../utils/textEncoding';

const ESTADO_LABEL = {
  pendiente: 'Pendiente',
  confirmado: 'Confirmado',
  en_preparacion: 'En preparación',
  entregado: 'Entregado',
  cancelado: 'Cancelado',
};

export default function PedidoReview({ id, pin, onUpdated, onBack }) {
  const [pedido, setPedido] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.getPedido(id);
        if (!cancelled) setPedido(res.data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, pin]);

  async function cambiarEstado(estado) {
    if (estado === 'cancelado' && !motivo.trim()) {
      setError('Escribe el motivo del rechazo');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await api.actualizarEstado(id, estado, estado === 'cancelado' ? motivo.trim() : undefined);
      setPedido(res.data);
      onUpdated?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="muted center">Cargando pedido...</p>;
  if (error && !pedido) return <p className="error center">{error}</p>;
  if (!pedido) return <p className="empty center">Pedido no encontrado</p>;

  const items = pedido.items || [];
  const puedeAprobar = pedido.estado === 'pendiente';
  const enBodega =
    pedido.estado === 'confirmado' ||
    pedido.estado === 'en_preparacion' ||
    pedido.estado === 'entregado';
  const separador = pedido.preparado_por_nombre;

  return (
    <div className="review">
      <header className="review-head">
        <button type="button" className="btn-back" onClick={onBack}>
          ← Lista
        </button>
        <div>
          <h2>{pedido.numero}</h2>
          <p className="muted">
            {pedidoClienteLabel(pedido)}
            {pedido.es_sede || pedido.padre_codigo ? (
              <span className="origen-pill"> · Sucursal</span>
            ) : null}
            {pedido.vendedor ? ` · ${pedido.vendedor}` : ''}
          </p>
          {(pedido.sucursal_direccion || pedido.ciudad || pedido.zona) ? (
            <p className="review-entrega-line">
              {pedido.sucursal_direccion || '—'}
              {pedido.ciudad || pedido.zona
                ? ` · ${[pedido.ciudad, pedido.zona].filter(Boolean).join(' · ')}`
                : ''}
            </p>
          ) : null}
        </div>
        <span className={`estado-badge estado-${pedido.estado}`}>
          {ESTADO_LABEL[pedido.estado] || pedido.estado}
        </span>
      </header>

      {error ? <p className="error banner">{error}</p> : null}
      {pedido.motivo_cancelacion ? (
        <p className="error banner">Motivo rechazo: {pedido.motivo_cancelacion}</p>
      ) : null}

      <div className="factura-panel" id="factura-print-area">
        <FacturaPreview
          numero={pedido.numero}
          fecha={pedido.created_at}
          pedido={pedido}
          items={items}
          subtotal={pedido.subtotal}
        />
      </div>

      <div className="review-actions">
        {puedeAprobar ? (
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={saving}
              onClick={() => cambiarEstado('confirmado')}
            >
              Aprobar pedido
            </button>
            <div className="reject-box">
              <input
                type="text"
                placeholder="Motivo del rechazo (obligatorio)"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
              <button
                type="button"
                className="btn-danger"
                disabled={saving}
                onClick={() => cambiarEstado('cancelado')}
              >
                Rechazar
              </button>
            </div>
          </>
        ) : null}
        <button type="button" className="btn-outline" onClick={() => printFactura('factura-print-area')}>
          Imprimir / PDF
        </button>
      </div>

      {enBodega ? (
        <div className="sep-card">
          <h4>Bodega</h4>
          {pedido.asignado_a_nombre ? (
            <p className="sep-row">
              <span>Asignado a</span>
              <strong>{pedido.asignado_a_nombre}</strong>
            </p>
          ) : null}
          {separador ? (
            <p className="sep-row">
              <span>Separado por</span>
              <strong>{separador}</strong>
            </p>
          ) : pedido.estado === 'confirmado' ? (
            <p className="muted sep-pending">Aún no lo han empezado a separar.</p>
          ) : null}
          {pedido.preparado_at ? (
            <p className="sep-row">
              <span>Completado</span>
              <strong>
                {new Date(pedido.preparado_at).toLocaleString('es-CO', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </strong>
            </p>
          ) : null}
        </div>
      ) : null}

      {pedido.estado_log?.length ? (
        <div className="estado-log">
          <h4>Historial</h4>
          <ul>
            {pedido.estado_log.map((ev, i) => (
              <li key={i}>
                <strong>{ESTADO_LABEL[ev.estado_nuevo] || ev.estado_nuevo}</strong>
                {ev.usuario_nombre ? <span> · {ev.usuario_nombre}</span> : null}
                {ev.nota ? <em> — {ev.nota}</em> : null}
                <small>
                  {new Date(ev.created_at).toLocaleString('es-CO', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}
                </small>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
